import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AdminSchoolHealthService } from '../../../services/admin/admin-school-health.service';
import { AdminSchoolService } from '../../../services/admin/admin-school.service';
import { SchoolGroupFilter } from '../../../services/school/school-groups.service';
import { SchoolHealthModel, CheckpointField } from '../../../models/school/school-health.model';
import { GroupModel, GroupActivitySummary, MIGRATION_STATUSES_NEEDING_ATTENTION, MIGRATION_STATUSES_NEEDING_REVIEW } from '../../../models/school/group.model';
import { ModalService } from '../../../shared/modals/modal/modal.service';

@Component({
  selector: 'app-admin-school-health-detail',
  templateUrl: './admin-school-health-detail.component.html',
  styleUrls: ['./admin-school-health-detail.component.css']
})
export class AdminSchoolHealthDetailComponent implements OnInit {

  schoolHealth: SchoolHealthModel = null;
  isLoading = false;

  groups: GroupModel[] = [];
  isLoadingGroups = false;
  groupSearch = '';

  isMigrationOpen = false;
  isRefreshing = false;
  isBackfilling = false;

  readonly CHECKPOINTS: { field: string; label: string; step: number }[] = [
    { field: 'licenseSetup',          label: 'License setup',      step: 1 },
    { field: 'groupsAndStudentSetup', label: 'Groups & students',  step: 2 },
    { field: 'teacherTraining',       label: 'Teacher training',   step: 3 },
    { field: 'bookOrder',             label: 'Book order',         step: 4 },
    { field: 'studentOnboarding',     label: 'Student onboarding', step: 5 },
  ];

  readonly SCHEDULABLE_FIELDS: string[] = ['teacherTraining', 'studentOnboarding'];

  openCheckpoint: string | null = null;
  // tslint:disable-next-line:max-line-length
  cpForms: { [field: string]:{ date: string; declined: boolean; confirmed?: boolean; newNoteText: string; isSaving: boolean; isAddingNote: boolean; numLicenses?: number | null; selectedGroupIds?: string[] } } = {};
  groupActivity: Map<string, GroupActivitySummary> = new Map();
  isLoadingActivity = false;


  private schoolYearId: string = null;
  private currentSchoolId: string = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private schoolHealthSv: AdminSchoolHealthService,
    private schoolSv: AdminSchoolService,
    private modalSv: ModalService
  ) {}

  ngOnInit() {
    this.schoolYearId = this.route.snapshot.params['schoolYearId'];
    const schoolHealthId = this.route.snapshot.params['schoolHealthId'];
    this.loadSchoolHealth(schoolHealthId);
  }

  loadSchoolHealth(schoolHealthId: string) {
    this.isLoading = true;
    this.schoolHealthSv.getSchoolHealthById(schoolHealthId).subscribe(health => {
      this.schoolHealth = health;
      this.currentSchoolId = health.schoolId;
      this.loadGroups(this.currentSchoolId);
      this.loadGroupsActivity();
      this.isLoading = false;
    }, () => {
      this.isLoading = false;
      this.modalSv.showAlertModal('Error', 'Could not load school health data. Please go back and try again.');
    });
  }

  loadGroups(schoolId: string) {
    this.isLoadingGroups = true;
    const filter = new SchoolGroupFilter();
    filter.includeContext = true;
    filter.includeObjective = true;
    this.schoolSv.fetchGroups(schoolId, filter).subscribe(groups => {
      this.groups = groups;
      this.isLoadingGroups = false;
    }, () => {
      this.isLoadingGroups = false;
    });
  }

  get filteredGroups(): GroupModel[] {
    let list = this.groups;
    if (this.groupSearch.trim()) {
      const q = this.groupSearch.toLowerCase();
      list = list.filter(g => (g.name || '').toLowerCase().includes(q));
    }
    return list;
  }

  loadGroupsActivity() {
    if (!this.currentSchoolId || !this.schoolYearId) { return; }
    this.isLoadingActivity = true;
    this.schoolHealthSv.getGroupsActivitySummary(this.currentSchoolId, this.schoolYearId).subscribe(list => {
      this.groupActivity = new Map(list.map(a => [a.groupId, a]));
      this.isLoadingActivity = false;
    }, () => {
      this.isLoadingActivity = false;
    });
  }

  refresh() {
    if (this.isRefreshing || !this.currentSchoolId) { return; }
    this.isRefreshing = true;
    let done = 0;
    const checkDone = () => { if (++done === 3) { this.isRefreshing = false; } };

    this.schoolHealthSv.getSchoolHealth(this.currentSchoolId).subscribe(health => {
      this.schoolHealth = health; checkDone();
    }, checkDone);

    const filter = new SchoolGroupFilter();
    filter.includeContext = true;
    filter.includeObjective = true;
    this.schoolSv.fetchGroups(this.currentSchoolId, filter).subscribe(groups => {
      this.groups = groups; checkDone();
    }, checkDone);

    this.schoolHealthSv.getGroupsActivitySummary(this.currentSchoolId, this.schoolYearId).subscribe(list => {
      this.groupActivity = new Map(list.map(a => [a.groupId, a])); checkDone();
    }, checkDone);
  }

  backfill() {
    if (this.isBackfilling || !this.currentSchoolId || !this.schoolHealth) { return; }
    this.isBackfilling = true;
    this.schoolHealthSv.backfillSchoolHealth(this.currentSchoolId, this.schoolHealth._id).subscribe(() => {
      this.isBackfilling = false;
      this.refresh();
    }, () => {
      this.isBackfilling = false;
    });
  }

  goBack() {
    if (this.schoolYearId) {
      this.router.navigate(['admin', 'schoolYears', this.schoolYearId, 'detail']);
    } else {
      this.router.navigate(['admin', 'schoolYears']);
    }
  }

  // ── School header helpers ──

  schoolInitials(name: string): string {
    if (!name) { return '?'; }
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) { return parts[0].charAt(0).toUpperCase(); }
    return (parts[0].charAt(0) + parts[1].charAt(0)).toUpperCase();
  }

  licenseStatusTag(h: SchoolHealthModel): { label: string; cls: string } {
    const now = new Date();
    if (h.licenseExpiresAt) {
      const exp = new Date(h.licenseExpiresAt);
      if (now > exp) { return { label: 'Expired', cls: 'tag-danger' }; }
      if (Math.ceil((exp.getTime() - now.getTime()) / 86400000) <= 30) {
        return { label: 'Expiring', cls: 'tag-warning' };
      }
    }
    if (h.lastAnswerDate) {
      if (Math.floor((now.getTime() - new Date(h.lastAnswerDate).getTime()) / 86400000) <= 90) {
        return { label: 'Active', cls: 'tag-active' };
      }
    }
    return { label: 'Inactive', cls: 'tag-gray' };
  }

  yearLabel(h: SchoolHealthModel): string {
    if (!h.licenseStartsAt || !h.licenseExpiresAt) { return ''; }
    const startYear = new Date(h.licenseStartsAt).getFullYear();
    const endYear   = new Date(h.licenseExpiresAt).getFullYear();
    return startYear === endYear ? String(startYear) : `${startYear}–${endYear}`;
  }

  licenseDateRange(h: SchoolHealthModel): string {
    if (!h.licenseStartsAt || !h.licenseExpiresAt) { return ''; }
    return `${this.formatShortDate(h.licenseStartsAt)} – ${this.formatShortDate(h.licenseExpiresAt)}`;
  }

  formatShortDate(iso: string): string {
    if (!iso) { return '—'; }
    return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  }

  activationPct(h: SchoolHealthModel): number {
    if (!h.numLicenses) { return 0; }
    return Math.round((h.numLicensesActivated || 0) / h.numLicenses * 100);
  }

  activeStudentsPct(h: SchoolHealthModel): number {
    if (!h.numStudents) { return 0; }
    return Math.min(100, Math.round((h.numActiveStudentsLast30Days || 0) / h.numStudents * 100));
  }

  active30dColor(h: SchoolHealthModel): string {
    const pct = this.activeStudentsPct(h);
    if (pct >= 30) { return '#16a34a'; }
    if (pct >= 10) { return '#ea580c'; }
    return '#dc2626';
  }

  lastActiveLabel(h: SchoolHealthModel): string {
    if (!h.lastAnswerDate) { return 'Never'; }
    const days = Math.floor((new Date().getTime() - new Date(h.lastAnswerDate).getTime()) / 86400000);
    if (days === 0) { return 'Today'; }
    if (days === 1) { return 'Yesterday'; }
    if (days < 30)  { return `${days} days ago`; }
    return this.formatShortDate(h.lastAnswerDate);
  }

  lastActiveColor(h: SchoolHealthModel): string {
    if (!h.lastAnswerDate) { return '#dc2626'; }
    const days = Math.floor((new Date().getTime() - new Date(h.lastAnswerDate).getTime()) / 86400000);
    if (days <= 7)  { return '#16a34a'; }
    if (days <= 30) { return '#ea580c'; }
    return '#dc2626';
  }

  // ── Metric helpers ──

  avgPerStudent(answers: number, activeStudents: number): string {
    if (!activeStudents) { return '—'; }
    return String(Math.round(answers / activeStudents));
  }

  groupParticipationPct(activeStudents: number, numStudents: number): number {

    if (!numStudents) { return 0; }
    return Math.min(100, Math.round((activeStudents || 0) / numStudents * 100));
  }

  formatAnswerTime(seconds: number): string {
    if (!seconds) { return '0h'; }
    const hours = seconds / 3600;
    return hours >= 10 ? `${Math.round(hours)}h` : `${Math.round(hours * 10) / 10}h`;
  }

  activeStudentsPct7d(h: SchoolHealthModel): number {
    if (!h.numStudents) { return 0; }
    return Math.round((h.numActiveStudentsLast7Days || 0) / h.numStudents * 100);
  }

  activeStudentsPctYear(h: SchoolHealthModel): number {
    if (!h.numStudents) { return 0; }
    return Math.round((h.numActiveStudentsThisYear || 0) / h.numStudents * 100);
  }

  // ── Setup / license helpers ──

  ratioColor(value: number, total: number, greenThreshold = 0.9, orangeThreshold = 0): string {
    if (!total) { return '#9ca3af'; }
    const ratio = value / total;
    if (ratio >= greenThreshold) { return '#16a34a'; }
    if (ratio > orangeThreshold) { return '#d97706'; }
    return '#dc2626';
  }

  ratioDotColor(value: number, total: number, greenThreshold = 0.9, orangeThreshold = 0): string {
    if (!total) { return '#d1d5db'; }
    const ratio = value / total;
    if (ratio >= greenThreshold) { return '#22c55e'; }
    if (ratio > orangeThreshold) { return '#f59e0b'; }
    return '#ef4444';
  }

  daysRemaining(h: SchoolHealthModel): string {
    if (!h.licenseExpiresAt) { return ''; }
    const days = Math.ceil((new Date(h.licenseExpiresAt).getTime() - new Date().getTime()) / 86400000);
    if (days < 0) { return 'Expired'; }
    return `${days} days remaining`;
  }

  licensePct(value: number, total: number): string {
    if (!total) { return '0%'; }
    return `${Math.round(value / total * 100)}%`;
  }

  // ── Group helpers ──

  groupTypeLabel(g: GroupModel): string {
    if (g.groupType === 'mixed') { return 'Mixed'; }
    if (g.groupType === 'specialEducation') { return 'Special Ed.'; }
    return g.grade ? `Grade ${g.grade}` : '';
  }

  groupRowClass(g: GroupModel): string {
    if (!g.migrationStatus || g.migrationStatus === 'RESOLVED') { return ''; }
    if ((MIGRATION_STATUSES_NEEDING_ATTENTION as string[]).includes(g.migrationStatus)) {
      return 'shd-group-row-warn';
    }
    return '';
  }

  groupLastActiveColor(label: string): string {
    if (label === 'Never') { return '#dc2626'; }
    if (label === 'Today' || label === 'Yesterday') { return '#16a34a'; }
    return '#d97706';
  }

  lastActiveLabelFromDate(iso: string | null): string {
    if (!iso) { return 'Never'; }
    const days = Math.floor((new Date().getTime() - new Date(iso).getTime()) / 86400000);
    if (days === 0) { return 'Today'; }
    if (days === 1) { return 'Yesterday'; }
    if (days < 30)  { return `${days} days ago`; }
    return this.formatShortDate(iso);
  }

  lastActiveColorFromDate(iso: string | null): string {
    if (!iso) { return '#dc2626'; }
    const days = Math.floor((new Date().getTime() - new Date(iso).getTime()) / 86400000);
    if (days <= 7)  { return '#16a34a'; }
    if (days <= 30) { return '#ea580c'; }
    return '#dc2626';
  }

  // ── Migration helpers ──

  get unresolvedCount(): number {
    if (this.schoolHealth) { return this.schoolHealth.numGroupsWithUnresolvedReview || 0; }
    return this.groups.filter(g => this.isUnresolved(g)).length;
  }

  get migrationGroups(): GroupModel[] {
    return this.groups.filter(g => !!g.migrationStatus);
  }

  migrationFlagSummary() {
    const attention = this.groups.filter(g => (MIGRATION_STATUSES_NEEDING_ATTENTION as string[]).includes(g.migrationStatus)).length;
    const review = this.groups.filter(g => (MIGRATION_STATUSES_NEEDING_REVIEW as string[]).includes(g.migrationStatus)).length;
    const resolved = this.groups.filter(g => g.migrationStatus === 'RESOLVED').length;
    const ok = this.groups.length - attention - review - resolved;
    return { attention, review, resolved, ok };
  }

  migrationBadgeInfo(status: string): { label: string; cls: string } {
    const map: Record<string, { label: string; cls: string }> = {
      'NO_OBJECTIVE':                  { label: 'No objective',          cls: 'mbadge-danger' },
      'OBJECTIVE_MISMATCH_LOWER':      { label: 'Mismatch (lower)',      cls: 'mbadge-danger' },
      'AT_MAX_OBJECTIVE':              { label: 'At max objective',      cls: 'mbadge-danger' },
      'MIXED_GROUP_NOT_ADVANCED':      { label: 'Mixed not advanced',    cls: 'mbadge-danger' },
      'OBJECTIVE_ADVANCED':            { label: 'Advanced',              cls: 'mbadge-warn' },
      'NON_STANDARD_GROUP':            { label: 'Non-standard',          cls: 'mbadge-warn' },
      'OBJECTIVE_CONFIRMED':           { label: 'Confirmed',             cls: 'mbadge-ok' },
      'RESOLVED':                      { label: 'Resolved',              cls: 'mbadge-ok' },
      'MIGRATION_SKIPPED_NO_STUDENTS': { label: 'Skipped (no students)', cls: 'mbadge-gray' },
      'GROUP_CREATED_MID_YEAR':        { label: 'Created mid-year',      cls: 'mbadge-gray' },
    };
    return map[status] || { label: status || '—', cls: 'mbadge-gray' };
  }

  isUnresolved(g: GroupModel): boolean {
    return (MIGRATION_STATUSES_NEEDING_ATTENTION as string[]).includes(g.migrationStatus)
      || g.migrationStatus === 'OBJECTIVE_ADVANCED';
  }

  resolveGroup(g: GroupModel) {
    this.schoolSv.resolveGroupMigrationContext(g.schoolId, g._id).subscribe(context => {
      const idx = this.groups.findIndex(x => x._id === g._id);
      if (idx >= 0) {
        this.groups[idx].migrationStatus = context.migrationStatus;
        this.groups = [...this.groups];
      }
      this.schoolHealthSv.getSchoolHealth(this.currentSchoolId).subscribe(health => {
        this.schoolHealth = health;
      });
    });
  }

  isResolvingAll = false;

  confirmResolveAllFlags() {
    const count = this.unresolvedCount;
    const message = `This will mark all open migration flags for this school as resolved, including flags from groups that no longer exist. Continue?`;
    this.modalSv.showChoiceModal(`Resolve ${count} flag${count !== 1 ? 's' : ''}`, message).subscribe(confirmed => {
      if (confirmed) {
        this.resolveAllFlags();
      }
    });
  }

  private resolveAllFlags() {
    this.isResolvingAll = true;
    this.schoolHealthSv.resolveAllMigrationFlags(this.schoolHealth._id).subscribe(health => {
      this.schoolHealth = health;
      this.groups.forEach(g => {
        if (this.isUnresolved(g)) {
          g.migrationStatus = 'RESOLVED';
        }
      });
      this.groups = [...this.groups];
      this.isResolvingAll = false;
    }, () => {
      this.isResolvingAll = false;
      this.modalSv.showAlertModal('Error', 'Could not resolve the flags. Please try again.');
    });
  }

  // ── Checkpoint helpers ──

  openCpPanel(field: string) {
    if (this.openCheckpoint === field) {
      this.openCheckpoint = null;
      return;
    }
    this.openCheckpoint = field;
    if (!this.cpForms[field]) {
      this.initCpForm(field);
    }
  }

  private initCpForm(field: string) {
    const cp = this.schoolHealth ? (this.schoolHealth as any)[field] as CheckpointField | null : null;
    const today = new Date().toISOString().substring(0, 10);
    const form: any = { date: today, declined: false, newNoteText: '', isSaving: false, isAddingNote: false };
    if (this.cpSupportsScheduling(field)) {
      form.confirmed = false;
    }
    if (cp) {
      if (cp.date) { form.date = cp.date.substring(0, 10); }
      form.declined = cp.declined || false;
      if (this.cpSupportsScheduling(field)) {
        form.confirmed = cp.confirmed || false;
      }
    }
    if (field === 'licenseSetup' && this.schoolHealth) {
      form.numLicenses = this.schoolHealth.numLicenses != null ? this.schoolHealth.numLicenses : null;
    }
    if (field === 'studentOnboarding' && this.schoolHealth) {
      const saved = this.schoolHealth.studentOnboarding ? (this.schoolHealth.studentOnboarding.groupIds || []) : [];
      form.selectedGroupIds = saved.slice();
    }
    this.cpForms[field] = form;
  }

  onDeclinedChange(field: string) {}

  clearOnboardingGroups() {
    const form = this.cpForms['studentOnboarding'];
    if (!form) { return; }
    form.selectedGroupIds = [];
  }

  toggleOnboardingGroup(groupId: string) {
    const form = this.cpForms['studentOnboarding'];
    if (!form || !form.selectedGroupIds) { return; }
    const idx = form.selectedGroupIds.indexOf(groupId);
    if (idx === -1) {
      form.selectedGroupIds.push(groupId);
    } else {
      form.selectedGroupIds.splice(idx, 1);
    }
  }

  isGroupSelected(groupId: string): boolean {
    const form = this.cpForms['studentOnboarding'];
    if (!form || !form.selectedGroupIds) { return false; }
    return form.selectedGroupIds.indexOf(groupId) !== -1;
  }

  cpSupportsScheduling(field: string): boolean {
    return this.SCHEDULABLE_FIELDS.indexOf(field) !== -1;
  }

  cpIsFutureDate(dateStr: string): boolean {
    if (!dateStr) { return false; }
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const d = new Date(dateStr);
    d.setHours(0, 0, 0, 0);
    return d.getTime() > today.getTime();
  }

  cpStatus(field: string): 'done' | 'declined' | 'warning' | 'scheduled' | 'needs-confirmation' | 'pending' {
    if (!this.schoolHealth) { return 'pending'; }
    const cp = (this.schoolHealth as any)[field] as CheckpointField | null;
    if (cp && cp.declined) { return 'declined'; }
    // Once someone has marked the groups checkpoint complete, the review has been signed off.
    if (field === 'groupsAndStudentSetup' && !(cp && cp.date) && (this.schoolHealth.numGroupsWithUnresolvedReview || 0) > 0) { return 'warning'; }
    if (cp && cp.date && this.cpSupportsScheduling(field)) {
      if (cp.confirmed) { return 'done'; }
      return this.cpIsFutureDate(cp.date) ? 'scheduled' : 'needs-confirmation';
    }
    if (cp && cp.date) { return 'done'; }
    return 'pending';
  }

  cpStepContent(step: number, status: 'done' | 'declined' | 'warning' | 'scheduled' | 'needs-confirmation' | 'pending'): string {
    if (status === 'done')     { return '✓'; }
    if (status === 'declined') { return '✕'; }
    if (status === 'warning')  { return '!'; }
    if (status === 'needs-confirmation') { return '?'; }
    return String(step);
  }

  cpStatusLabel(status: 'done' | 'declined' | 'warning' | 'scheduled' | 'needs-confirmation' | 'pending'): string {
    if (status === 'done')     { return 'Completed'; }
    if (status === 'declined') { return 'Declined'; }
    if (status === 'warning')  { return 'Migration flags pending'; }
    if (status === 'scheduled') { return 'Scheduled'; }
    if (status === 'needs-confirmation') { return 'Awaiting confirmation'; }
    return 'Not started';
  }

  cpDateSubtext(field: string, status: 'done' | 'declined' | 'warning' | 'scheduled' | 'needs-confirmation' | 'pending'): string {
    if (!this.schoolHealth) { return '—'; }
    const cp = (this.schoolHealth as any)[field] as CheckpointField | null;
    if (field === 'groupsAndStudentSetup' && status === 'warning') {
      const count = this.schoolHealth.numGroupsWithUnresolvedReview || 0;
      if (count === 1) { return '1 group needs review'; }
      return `${count} groups need review`;
    }
    if (status === 'scheduled') { return `Scheduled for ${this.cpFormatDate(cp.date)}`; }
    if (status === 'needs-confirmation') { return `Was scheduled for ${this.cpFormatDate(cp.date)} — confirm below`; }
    if (status === 'pending') { return '—'; }
    if (cp && cp.date) { return this.cpFormatDate(cp.date); }
    return '—';
  }

  cpFormatDate(iso: string): string {
    if (!iso) { return '—'; }
    return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  }

  cpNoteCount(field: string): number {
    if (!this.schoolHealth) { return 0; }
    const cp = (this.schoolHealth as any)[field] as CheckpointField | null;
    if (!cp || !cp.notes) { return 0; }
    return cp.notes.length;
  }

  cpSummaryText(): string {
    if (!this.schoolHealth) { return ''; }
    let done = 0;
    let warning = 0;
    let declined = 0;
    let pending = 0;
    let scheduled = 0;
    let needsConfirmation = 0;
    for (const cp of this.CHECKPOINTS) {
      const s = this.cpStatus(cp.field);
      if (s === 'done')     { done++; }
      if (s === 'warning')  { warning++; }
      if (s === 'declined') { declined++; }
      if (s === 'pending')  { pending++; }
      if (s === 'scheduled') { scheduled++; }
      if (s === 'needs-confirmation') { needsConfirmation++; }
    }
    const parts: string[] = [];
    if (done > 0)     { parts.push(`${done} done`); }
    if (warning === 1) { parts.push('1 migration flag'); }
    if (warning > 1)  { parts.push(`${warning} migration flags`); }
    if (needsConfirmation === 1) { parts.push('1 awaiting confirmation'); }
    if (needsConfirmation > 1) { parts.push(`${needsConfirmation} awaiting confirmation`); }
    if (scheduled === 1) { parts.push('1 scheduled'); }
    if (scheduled > 1) { parts.push(`${scheduled} scheduled`); }
    if (declined > 0) { parts.push(`${declined} declined`); }
    if (pending > 0)  { parts.push(`${pending} pending`); }
    return parts.join(' · ');
  }

  saveCp(field: string) {
    if (!this.schoolHealth) { return; }
    const form = this.cpForms[field];
    if (!form || form.isSaving) { return; }
    form.isSaving = true;

    const cpData: { date: string | null; declined: boolean; confirmed?: boolean; groupIds?: string[] } = { date: form.date || null, declined: form.declined };
    if (this.cpSupportsScheduling(field)) {
      cpData.confirmed = !!form.confirmed;
    }
    if (field === 'studentOnboarding' && form.selectedGroupIds !== undefined) {
      cpData.groupIds = form.selectedGroupIds;
    }

    let licenseChanged = false;
    if (field === 'licenseSetup') {
      const origNum = this.schoolHealth.numLicenses != null ? this.schoolHealth.numLicenses : null;
      if (form.numLicenses !== origNum) {
        licenseChanged = true;
      }
    }

    const done = (health: SchoolHealthModel) => {
      this.schoolHealth = health;
      delete this.cpForms[field];
      form.isSaving = false;
      if (this.openCheckpoint === field) {
        this.openCheckpoint = null;
      }
    };

    const fail = () => { form.isSaving = false; };

    const healthId = this.schoolHealth._id;

    if (!licenseChanged) {
      this.schoolHealthSv.updateCheckpoint(healthId, field, cpData).subscribe(done, fail);
      return;
    }

    const licenseData = {
      numLicenses: form.numLicenses != null ? form.numLicenses : this.schoolHealth.numLicenses,
    };

    this.schoolSv.updateSchool({ _id: this.schoolHealth.schoolId, ...licenseData } as any).subscribe(() => {
      this.schoolHealthSv.syncLicense(healthId, licenseData).subscribe(() => {
        this.schoolHealthSv.updateCheckpoint(healthId, field, cpData).subscribe(done, fail);
      }, fail);
    }, fail);
  }

  quickConfirm(field: string, event: Event) {
    event.stopPropagation();
    if (!this.cpForms[field]) {
      this.initCpForm(field);
    }
    const form = this.cpForms[field];
    form.confirmed = true;
    this.saveCp(field);
  }

  addCpNote(field: string) {
    if (!this.schoolHealth) { return; }
    const form = this.cpForms[field];
    if (!form || form.isAddingNote || !form.newNoteText.trim()) { return; }
    form.isAddingNote = true;
    this.schoolHealthSv.addCheckpointNote(this.schoolHealth._id, field, form.newNoteText.trim()).subscribe(health => {
      this.schoolHealth = health;
      form.newNoteText = '';
      form.isAddingNote = false;
    }, () => {
      form.isAddingNote = false;
    });
  }

}
